import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Deals from './pages/Deals';
import DealDetail from './pages/DealDetail';
import Clients from './pages/Clients';
import Prospects from './pages/Prospects';
import ClientDetail from './pages/ClientDetail';
import Contacts from './pages/Contacts';
import Partners from './pages/Partners';
import Activities from './pages/Activities';
import Reports from './pages/Reports';
import ServicePillars from './pages/ServicePillars';
import Users from './pages/Users';
import AuditLogs from './pages/AuditLogs';
import DataImport from './pages/DataImport';
import Targets from './pages/Targets';
import TenderScout from './pages/TenderScout';
import TenderList from './pages/TenderList';
import TenderDetail from './pages/TenderDetail';
import TenderSources from './pages/TenderSources';
import TenderSettings from './pages/TenderSettings';
import CompanyList from './pages/CompanyList';
import AIHistory from './pages/AIHistory';
import TenderCompare from './pages/TenderCompare';

// Layout
import Layout from './components/Layout';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" replace /> : <Login />} 
      />
      
      <Route 
        path="/register" 
        element={user ? <Navigate to="/" replace /> : <Register />} 
      />
      
      <Route 
        path="/forgot-password" 
        element={user ? <Navigate to="/" replace /> : <ForgotPassword />} 
      />
      
      <Route 
        path="/reset-password" 
        element={user ? <Navigate to="/" replace /> : <ResetPassword />} 
      />
      
      <Route element={<Layout />}>
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/deals" 
          element={
            <ProtectedRoute>
              <Deals />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/deals/:id" 
          element={
            <ProtectedRoute>
              <DealDetail />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/clients" 
          element={
            <ProtectedRoute>
              <Clients category="client" />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/prospects" 
          element={
            <ProtectedRoute>
              <Prospects />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/clients/:id" 
          element={
            <ProtectedRoute>
              <ClientDetail />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/prospects/:id" 
          element={
            <ProtectedRoute>
              <ClientDetail />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/contacts" 
          element={
            <ProtectedRoute>
              <Contacts />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/partners" 
          element={
            <ProtectedRoute>
              <Partners />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/activities" 
          element={
            <ProtectedRoute>
              <Activities />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/reports" 
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings/service-pillars" 
          element={
            <ProtectedRoute adminOnly>
              <ServicePillars />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings/users" 
          element={
            <ProtectedRoute adminOnly>
              <Users />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings/audit-logs" 
          element={
            <ProtectedRoute adminOnly>
              <AuditLogs />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings/data-import" 
          element={
            <ProtectedRoute adminOnly>
              <DataImport />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/settings/targets" 
          element={
            <ProtectedRoute adminOnly>
              <Targets />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout" 
          element={
            <ProtectedRoute>
              <TenderScout />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout/tenders" 
          element={
            <ProtectedRoute>
              <TenderList />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout/tenders/:id" 
          element={
            <ProtectedRoute>
              <TenderDetail />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/tender-scout/compare" 
          element={
            <ProtectedRoute>
              <TenderCompare />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout/companies" 
          element={
            <ProtectedRoute>
              <CompanyList />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout/sources" 
          element={
            <ProtectedRoute adminOnly>
              <TenderSources />
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/tender-scout/settings" 
          element={
            <ProtectedRoute adminOnly>
              <TenderSettings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ai-history" 
          element={
            <ProtectedRoute>
              <AIHistory />
            </ProtectedRoute>
          } 
        />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
